<template>
  <div class="word-reader">
    <div
      ref="wordContent"
      class="word-content"
    >
      <div
        v-if="!docContent"
        class="loading"
      >
        Loading document...
      </div>
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-else
        v-html="highlightedDocContent"
      />
    </div>
  </div>
</template>

<script>
import mammoth from 'mammoth';
import DOMPurify from "dompurify";

/**
 * WordViewer component for displaying Word documents (.docx)
 * Handles text selection and document conversion to HTML
 */
export default {
    name: 'WordViewer',
    props: {
        docxFile: {
            type: Blob,
            required: true
        },
        activeSentenceText: {
            type: String,
            default: null
        }
    },
    emits: ['documentLoaded', 'textSelected', 'textSelectedOnTextReader'],
    data() {
        return {
            docContent: null,
            selectedText: null,
            touchStartX: 0,
            touchStartY: 0,
        };
    },

    computed: {
        highlightedDocContent() {
            if (!this.docContent || !this.activeSentenceText) return this.docContent;
            const escaped = this.activeSentenceText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return this.docContent.replace(
                new RegExp(escaped, 'g'),
                '<mark class="voice-highlight">$&</mark>'
            );
        }
    },

    watch: {
        docxFile: {
            handler() {
                this.$nextTick(() => {
                    this.initializeViewer();
                });
            },
            immediate: false
        }
    },

    mounted() {
        if (this.docxFile) {
            this.initializeViewer();
        }
    },
    methods: {
        async initializeViewer() {
            try {
                const arrayBuffer = await this.docxFile.arrayBuffer();
                
                const result = await mammoth.convertToHtml({
                    arrayBuffer: arrayBuffer
                });

                this.docContent = DOMPurify.sanitize(result.value);
                
                // Setup text selection after DOM update
                this.$nextTick(() => {
                    this.setupTextSelectionListener();
                });
                
                this.$emit('documentLoaded');
            } catch (error) {
                console.error('Error loading Word document:', error);
                this.docContent = '<p>Error loading document</p>';
            }
        },

        setupTextSelectionListener() {
            if (!this.$refs.wordContent) return;

            try {
                this.$refs.wordContent.addEventListener('mouseup', () => {
                    const selectedText = window.getSelection().toString();
                    if (selectedText && selectedText.trim().length > 0) {
                        this.selectedText = selectedText;
                        this.$emit('textSelected', {
                            text: selectedText,
                            pageNumber: 1,
                        });
                    }
                });

                this.$refs.wordContent.addEventListener('touchstart', (e) => {
                    const t = e.touches[0];
                    if (t) {
                        this.touchStartX = t.clientX;
                        this.touchStartY = t.clientY;
                    }
                    window.getSelection()?.removeAllRanges();
                }, { passive: true });

                this.$refs.wordContent.addEventListener('touchend', (e) => {
                    const t = e.changedTouches[0];
                    if (t) {
                        const dx = Math.abs(t.clientX - this.touchStartX);
                        const dy = Math.abs(t.clientY - this.touchStartY);
                        if (dx > 8 || dy > 8) return;
                    }

                    setTimeout(async () => {
                        // Emit the event to the parent component
                        this.$emit('textSelectedOnTextReader', e);
                    }, 50);
                console.log("Text selection listener registered for Word document");
                }, { passive: true });
            } catch (err) {
                console.log("Error setting up text selection listener:", err.message);
            }

        },

        getCurrentPage() {
            return 1;
        }
    }
};
</script>

<style scoped>
.word-reader {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    background-color: var(--color-bg);
    border: 1px solid var(--color-primary-mid);
    border-radius: 10px;
}

.word-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px;
    background-color: white;
    user-select: text;
    border-radius: 10px
}

.word-content::-webkit-scrollbar {
    width: 6px;
}

.word-content::-webkit-scrollbar-track {
    background: var(--color-surface-raised);
}

.word-content::-webkit-scrollbar-thumb {
    background: var(--color-aurora-start);
    border-radius: 3px;
}

.word-content::-webkit-scrollbar-thumb:hover {
    background: var(--color-aurora-end);
}

.word-content :deep(p) {
    margin: 0.5em 0;
    line-height: 1.6;
}

.word-content :deep(h1),
.word-content :deep(h2),
.word-content :deep(h3),
.word-content :deep(h4),
.word-content :deep(h5),
.word-content :deep(h6) {
    margin: 0.8em 0 0.4em 0;
    font-weight: bold;
}

.word-content :deep(ul),
.word-content :deep(ol) {
    margin: 0.5em 0;
    padding-left: 2em;
}

.word-content :deep(li) {
    margin: 0.2em 0;
}

.word-content :deep(table) {
    border-collapse: collapse;
    margin: 1em 0;
    width: 100%;
}

.word-content :deep(table td),
.word-content :deep(table th) {
    border: 1px solid #ddd;
    padding: 0.5em;
}

.word-content :deep(table th) {
    background-color: #f5f5f5;
    font-weight: bold;
}

.loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #999;
    font-size: 16px;
}

.word-content :deep(mark.voice-highlight) {
    background-color: #ffe066;
    color: inherit;
    border-radius: 2px;
    padding: 0 1px;
}
</style>
